import { useState, useEffect, useLayoutEffect, ReactNode } from "react";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import MainTable from "../../../components/tables/MainTable";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import endpoints from "../../../services/endpoints";
import ContentWrapper from "../../../components/Add/ContentWrapper";
import CustomModal from "../../../components/CustomModal";
import ClearDueModal from "../../../components/Models/ClearDueModal"
import useApi from "../../../services/useApi";
import PinVerificationModel from "../../../components/Models/PinVerificationModel";
import ETopUpTransactionReceipt from "../../Profile/RequestRaised/ETopUpTransactionReciept";
import { fetchAuthSession } from "@aws-amplify/auth";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
type ETopUpViewProps = {
  defaultPaymentType?: "advance" | "paid";
  defaultWallet?: string;
  headerContent?: ReactNode;
};
export default function ETopUpTransactions({
  defaultPaymentType = "advance",
  defaultWallet = "E Top Up",
}: ETopUpViewProps) {
  const initialFormData = {
    payment_type: defaultPaymentType,
    choose_wallet: defaultWallet,
    amount: "",
    request_from: {},
    pin_interval: "",
    isDontAskAgainChecked: false,
    receiver_payment_source: "Bank",
    receiver_bank: {},
    sender_payment_source: "Bank",
    sender_bank: {},
    transaction_receipt: "",
    choose_platform: "",
    hesabpay_account_number: "",
    associated_phone_number: "",
  };
  const navigate = useNavigate();
  const { getAPI, PatchAPI } = useApi();
  const { notEnglish } = useTranslation({});
  const isTransitioning = false;
  const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
  const [loading, setLoading] = useState(false);
  const { raiseETopUpRequest } = endpoints;
  const searchPlaceholder = "Transaction ID";
  const { translate } = useTranslation(adminContents);
  const { id } = useParams()
  const location = useLocation();
  const [clearDueModal, setClearDueModal] = useState(false)
  const [text, setText] = useState('')
  const [textError, setTextError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isVerificationModelOpen, setIsVerificationModelOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [pin, setPin] = useState<string[]>(new Array(4).fill(""));
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isTransactionReceipt, setIsTransactionReceipt] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  // const [popUpLoading, setPopUpLoading] = useState(false);
  // const [isPopupPage, setIsOpenPage] = useState(2);
  const [responseData, setResponseData] = useState({});
  const [remainingAmount, setRemainingAmount] = useState('')
  const [dueCleared, setDueCleared] = useState(false)
  const [duePendingAmount, setDuePendingAmount] = useState('')
  const [isZeroPayment, setIsZeroPayment] = useState(false)

  const TransactionDetails = useSelector((state: RootState) => state.transactionDetails.selectedTransaction);
  // Dummy static text for translations
  const staticText = {
    emptyScreenText: {
      heading1: translate("There are no advance payment added yet"),
    },
  };

  // Table column definitions
  const TableDetails: { [key: string]: TableDetailsItem } = {
    "Transaction ID": {
      id: "transaction_id",
      className: "min-w-[180px]",
    },
    "Requested Date": {
      id: "transaction_date",
      className: "min-w-[197px]",
      custom: ["transaction_date"],
      sort: true,
    },
    "Requested Amount": {
      id: "advance_amount",
      className: "min-w-[150px]",
      custom: ["advance_amount"]
    },
    "Payable Amount": {
      id: "payable_amount",
      className: "min-w-[150px]",
      custom: ["payable_amount"]
    },
    "Action": {
      id: "actions",
      className: "min-w-[100px]",
      actionTypes: ["payment"],
    },
  };
  const handleVerificationClose = () => {
    setIsVerificationModelOpen(false);
    setPin(new Array(4).fill(""));
    setErrorMessage("");
    // setPopUpLoading(false);
    setFormData((prevData) => ({
      ...prevData,
      ["isDontAskAgainChecked"]: false,
      ["pin_interval"]: "",
    }));
  };
  const handleClearDueConfirmation = () => {
    if (textError) {
      return;
    }
    const enteredAmount = parseFloat(text);
    const payableAmount = parseFloat(selectedTransaction.payable_amount) || 0;
    const confirmedAmount = parseFloat(TransactionDetails?.confirmed_amount) || 0;

    if (isNaN(enteredAmount) || enteredAmount < 0) {
      setTextError("Please enter a valid amount");
      return;
    }

    // Close the input modal
    setClearDueModal(false);

    // Special case: if user enters 0, no payment is made toward due
    if (enteredAmount === 0) {
      setRemainingAmount(confirmedAmount); // All confirmed amount converted to e-top up
      setDuePendingAmount(payableAmount); // Due remains unchanged
      setDueCleared(false);
      setIsZeroPayment(true);
      setIsModalOpen(true);
      return;
    }

    // Calculate remaining due after payment
    const remainingDue = payableAmount - enteredAmount;

    // Determine if due is fully cleared
    const isDueCleared = remainingDue <= 0;

    // Calculate how much of confirmed amount remains after covering the due
    const remainingCA = confirmedAmount - enteredAmount;

    setRemainingAmount(Math.max(0, remainingCA));
    setDuePendingAmount(Math.max(0, remainingDue));
    setDueCleared(isDueCleared);
    setIsZeroPayment(false);
    setIsModalOpen(true);
  };
  const resetAllModals = () => {
    setClearDueModal(false);
    setIsModalOpen(false);
    setIsVerificationModelOpen(false);
    // setIsOpenPage(1);
    setPin(new Array(4).fill(""));
    setText('');
    setTextError('');
    setSelectedTransaction(null);
    setErrorMessage("");
    // setPopUpLoading(false);
    setVerifyLoading(false);
  };
  const handleSubmitPopup = async () => {
    // setPopUpLoading(true);
    setLoading(true);
    try {
      // Check if PIN verification is needed based on expiry timestamp
      const user = await fetchAuthSession({ forceRefresh: true });
      const timestamp = user?.tokens?.idToken?.payload["custom:pin_expiry_timestamp"];

      if (timestamp) {
        const inputDate = new Date(timestamp);
        const currentDate = new Date();

        if (inputDate < currentDate) {
          // PIN expired, show verification modal
          setIsVerificationModelOpen(true);
        } else {
          // PIN still valid, proceed without verification
          handleClickVerify(false);
        }
      } else {
        // No timestamp, show verification modal
        setIsModalOpen(false);
        setIsVerificationModelOpen(true);
      }
    } catch (error) {
      console.error("Error checking PIN expiry:", error);
      // If there's an error, default to showing the verification modal
      setIsVerificationModelOpen(true);
    } finally {
      // setPopUpLoading(false);
      setLoading(false);
    }
  };
  const handleClickDone = () => {
    setResponseData({});
    setText('');
    setFormData(initialFormData);
    setIsTransactionReceipt(false);
    setIsModalOpen(false)
    resetAllModals()
    navigate(`/e-top-up-request/e-top-up`)
  };

  const handleClickVerify = async (isPin: boolean) => {
    setVerifyLoading(true);
    setErrorMessage("");

    try {
      // Validate PIN if required
      if (isPin) {
        if (pin.includes("")) {
          setErrorMessage("This field is required");
          setVerifyLoading(false);
          return;
        }

        if (formData.isDontAskAgainChecked && formData.pin_interval === "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            pin_interval: "This field is required",
          }));
          setVerifyLoading(false);
          return;
        }
      }

      // Prepare payload for API request
      let payload: { [key: string]: any } = {
        confirmed_amount: Number(TransactionDetails.confirmed_amount),
        e_topup_stock_amount: Number(remainingAmount),
        due_transaction_id: (Number(text)) === 0  ? null : selectedTransaction?.id,
        due_amount: Number(text),
        note: TransactionDetails.note,
        pin: isPin ? pin.join("") : "", // Only include PIN if verification was shown
        pin_interval: isPin ? formData?.pin_interval : "",
      };

      // Send API request
      const endpoint = `cash-in/approve/${id}`;
      const response = await PatchAPI(endpoint, payload);

      if (response.error) {
        // Handle API error
        setErrorMessage(response?.data?.response?.data?.message || "An error occurred");
      } else {
        // Handle successful response
        setResponseData(response?.data?.data);
        setIsTransactionReceipt(true);
        setIsVerificationModelOpen(false);
        setText("");
      }
    } catch (error) {
      // Handle unexpected errors
      console.error("Verification error:", error);
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      // Reset loading states
      setVerifyLoading(false);
      // setPopUpLoading(false);
    }
  };

  const handleInputChange = (key: string, value: string | boolean | any[]) => {
    // Validate amount field to ensure it's a valid number with up to 2 decimal places
    if (key === "amount" && typeof value === "string") {
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(value)) return;
    }

    // Validate phone number format for associated_phone_number field
    if (
      key === "associated_phone_number" &&
      typeof value === "string" &&
      value.length > 0
    ) {
      // This regex ensures:
      // 1. "+" symbol only allowed at the beginning
      // 2. No consecutive spaces or hyphens
      // 3. Only numbers, spaces, and hyphens allowed after optional leading "+"
      const regex = /^\+?(?!.*[ -][ -])(?!.*[+].*[+])[0-9 -]*$/;
      if (!regex.test(value)) return;
    }

    // Remove spaces from hesabpay account number
    if (key === "hesabpay_account_number") {
      value = value.replace(/\s/g, ""); // Remove spaces
    }

    // Reset form when payment type changes
    if (key === "payment_type") {
      setText("");
      setFormData(initialFormData);
      setErrors({});
    }

    // Handle special case: when receiver payment source is Cash, set sender payment source to Cash too
    if (key === "receiver_payment_source" && value === "Cash") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_payment_source"]: value,
      }));
    }

    // Reset bank fields when receiver payment source changes
    if (key === "receiver_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        ["receiver_bank"]: {},
        ["sender_bank"]: {},
        ["transaction_receipt"]: "",
      }));
    }

    // Reset sender bank when sender payment source changes
    if (key === "sender_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_bank"]: {},
      }));
    }

    // When receiver payment source is Bank, set sender payment source to Bank too
    if (key === "receiver_payment_source" && value === "Bank") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_payment_source"]: value,
      }));
    }

    // Update form data with new value
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));

    // Clear error for the updated field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: "",
    }));

    // Reset related fields when platform changes
    if (key === "choose_platform") {
      setFormData((prevData) => ({
        ...prevData,
        hesabpay_account_number: "",
        associated_phone_number: "",
        transaction_receipt: "",
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        hesabpay_account_number: "",
        associated_phone_number: "",
        transaction_receipt: "",
      }));
    }

    // Reset all payment-related fields when receiver payment source changes
    if (key === "receiver_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        receiver_bank: {},
        sender_bank: {},
        transaction_receipt: "",
        choose_platform: "",
        hesabpay_account_number: "",
        associated_phone_number: "",
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        receiver_bank: "",
        sender_bank: "",
        transaction_receipt: "",
        choose_platform: "",
        hesabpay_account_number: "",
        associated_phone_number: "",
      }));
    }
  };
  // Handle table actions (view, edit, etc)
  const handleActions = (_id, actionType, transaction) => {
    console.log('Transaction selected:', transaction);
    setSelectedTransaction(transaction);
    setClearDueModal(true);

    try {
      const payableStr = transaction?.payable_amount;
      const payableAmount = payableStr && !isNaN(payableStr) ? parseFloat(payableStr) : NaN;

      let confirmedAmount = 0;
      if (TransactionDetails.confirmed_amount) {
        if (typeof TransactionDetails.confirmed_amount === 'number') {
          confirmedAmount = TransactionDetails.confirmed_amount;
        } else {
          confirmedAmount = parseFloat(TransactionDetails.confirmed_amount.replace(/[^0-9.]/g, ''));
        }
      }

      console.log('Comparison - Payable:', payableAmount, 'Confirmed:', confirmedAmount);

      if (!isNaN(payableAmount) && !isNaN(confirmedAmount)) {
        setText((payableAmount > confirmedAmount ? confirmedAmount : payableAmount).toFixed(2));
      } else if (!isNaN(payableAmount)) {
        setText(payableAmount.toFixed(2));
      } else if (!isNaN(confirmedAmount)) {
        setText(confirmedAmount.toFixed(2));
      } else {
        setText('');
      }
    } catch (error) {
      console.error('Error in prefill logic:', error);
      setText('');
    }
  };


  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Make API call
      const response = await getAPI(raiseETopUpRequest + `?receiver_id=${TransactionDetails?.receiver_id}&status=Approved&type=advance`);
      if (!response?.error) {
        console.log('API Response:', response);
        const transactionsData = response?.data?.data || [];

        // Log the first transaction to debug values
        if (transactionsData.length > 0) {
          console.log('Sample transaction:', transactionsData[0]);
          console.log('Payable amount type:', typeof transactionsData[0].payable_amount); // Should be string or number
        }
        const normalized = transactionsData.map(t => ({
          ...t,
          payable_amount: t.payable_amount?.toString() || "0"
        }));
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  useLayoutEffect(() => {
    fetchTransactions();
  }, []);

  const handleProceed = () => {
    console.log("Processing payment with amount:", text);

    // Reset popupPage state to ensure consistent flow
    // setIsOpenPage(1);

    // Clear any previous errors
    setErrorMessage("");

    // Show verification modal immediately
    handleSubmitPopup();
  }

  const handleClose = () => {
    setIsModalOpen(false)
  }
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const requestedAmount = selectedTransaction ?
      parseFloat(selectedTransaction.payable_amount) :
      parseFloat(TransactionDetails.requested_amount?.replace(/[^0-9.]/g, '') || 0);

    const value = e.target.value;

    // Only allow numbers and at most one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      // Validate decimal places (max 2)
      const parts = value.split('.');
      if (parts.length > 1 && parts[1].length > 2) {
        return; // Prevent input if more than 2 decimal places
      }

      // Validate max length (max 15 characters)
      if (value.length > 15) {
        return; // Prevent input if longer than 15 characters
      }

      // Validate integer part (max 12 digits)
      if (parts[0].length > 12) {
        return; // Prevent input if more than 12 digits before decimal
      }

      setText(value);
      if (value !== '' && !value.startsWith('0.')) {
        // Remove leading zeros but preserve "0." for decimal values less than 1
        const normalizedValue = value.replace(/^0+(\d)/, '$1');
        setText(normalizedValue);
        setTextError(validateAmount(normalizedValue, requestedAmount));
      } else {
        setText(value);
        setTextError(validateAmount(value, requestedAmount));
      }
      setTextError(validateAmount(value, requestedAmount)); // Pass the requested amount
    }
  };

  const validateAmount = (value: string, requestedAmount?: number): string => {
    const confirmed_amount = parseFloat(TransactionDetails?.confirmed_amount);
    const numValue = parseFloat(value);

    if (!value.trim()) {
      return "This field is required";
    }

    if (isNaN(numValue)) {
      return "Invalid amount";
    }

    if (confirmed_amount !== undefined && numValue > confirmed_amount) {
      return "Amount cannot be greater than confirmed amount";
    }

    if (requestedAmount !== undefined && numValue > requestedAmount) {
      return "Amount cannot be greater than payable amount";
    }

    return "";
  };

  return (
    <div dir={notEnglish ? "rtl" : "ltr"} className={`overflow-auto max-h-[100%] ${getFirefoxCssFix('scroll')}`}>
      <Breadcrumbs
        navPath={["E Top Up Request", "List of E Top Up Request Raised", "View Transaction Details", "List of all Pending Amount"]}
        links={pathname ? [pathname, pathname, `/e-top-up-request/e-top-up/${id}`] : [
          "/raise-e-top-up-request",
          "/e-top-up-request",
          `/e-top-up-request/e-top-up/${id}`
        ]}
        heading="List of all Pending Amount"
      />

      {/* Customer Details Section */}
      <div className="mt-1.5 bg-white rounded-lg p-6">
        <div className="flex flex-wrap -mx-5">
          {/* Recipient User Code */}
          <div className="px-5 w-1/3 mb-6">
            <h1 className="text-body2 text-primary_text">
              Recipient User Code
            </h1>
            <p
              data-testid="recipient-user-code"
              dir="ltr"
              className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
            >
              {TransactionDetails?.recipient_user_code ?? "N/A"}
            </p>
          </div>

          {/* Recipient User Name */}
          <div className="px-5 w-1/3 mb-6">
            <h1 className="text-body2 text-primary_text">
              Recipient User Name
            </h1>
            <p
              data-testid="recipient-user-name"
              dir="ltr"
              className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
            >
              {TransactionDetails?.recipient_user_name ?? "N/A"}
            </p>
          </div>

          {/* Requested Amount */}
          <div className="px-5 w-1/3 mb-6">
            <h1 className="text-body2 text-primary_text">
              Requested Amount
            </h1>
            <p
              data-testid="requested-amount"
              dir="ltr"
              className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
            >
              AFA {TransactionDetails?.requested_amount ?? "N/A"}
            </p>
          </div>

          {/* Confirmed Amount (Previously labeled as Wallet Type) */}
          <div className="px-5 w-1/3 mb-6">
            <h1 className="text-body2 text-primary_text">
              Confirmed Amount
            </h1>
            <p
              data-testid="confirmed-amount"
              dir="ltr"
              className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
            >
              AFA {TransactionDetails?.confirmed_amount != null
                ? Number(TransactionDetails.confirmed_amount).toFixed(2)
                : "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="mt-1.5">
        <ContentWrapper>
          <div className="mt-6 px-5">
            <div className="flex items-center">
              <h2 className="text-lg font-medium relative">
                List of all Pending Amount
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#1F1F1F]"></span>
              </h2>
            </div>
          </div>
          <MainTable
            staticText={staticText}
            mainEndPoint={raiseETopUpRequest}
            TableDetails={TableDetails}
            handleActions={handleActions}
            params={{
              receiver_id: TransactionDetails?.receiver_id, // Replace with actual variable
              status: "Approved",
              type: "advance"
            }}
            searchPlaceholder={`Search by ${searchPlaceholder}`}
            placeholderClassName={`search-placeholder ${isTransitioning ? "transitioning" : ""}`}
            filters={false}
            statusNotFilter={false}
            exporting={false}
            stickyStyleTableHeader={"top-[0.5px]"}
            search={false}
            title="List of all Pending Amount"
          />
        </ContentWrapper>
      </div>

      {clearDueModal && selectedTransaction && (
        <CustomModal
          isOpen={clearDueModal}
          title={"Confirm Payment"}
          subText={"Please enter the amount you wish to pay"}
          primaryButtonText={"Confirm"}
          secondaryButtonText="Cancel"
          handelSubmit={handleClearDueConfirmation}
          buttonColor={"bg-p_primary"}
          textArea={false}
          secondaryButton={false}
          onHandleClose={() => {
            setClearDueModal(false);
            setText('');
            setTextError('');
            setSelectedTransaction(null);
          }}
          onClose={() => { }}
        >
          <div className="absolute top-4 right-4">
            <button
              onClick={() => {
                setClearDueModal(false);
                setText('');
                setTextError('');
                setSelectedTransaction(null);
              }}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <img src="/images/close.svg" />
            </button>
          </div>
          <div className="mb-4">
            <label className="text-body1 text-primary_text flex items-center">
              Amount<span className="text-negative ml-1">*</span>
            </label>
            <input
              type="text"
              value={text}
              onChange={handleAmountChange}
              className={`w-full p-2 border ${textError ? 'border-negative' : 'border-outline'} rounded-md mt-1`}
              placeholder="Enter amount"
              maxLength={15}
              data-testid={"pay_due_amount"}
            />
            {textError && <p className="text-negative text-sm mt-1">{textError}</p>}
          </div>
        </CustomModal>
      )}

      {isModalOpen && selectedTransaction && (
        <ClearDueModal
          isOpen={isModalOpen}
          remainingAmount={parseFloat(selectedTransaction.payable_amount) - parseFloat(text)}
          onProceed={handleProceed}
          dueCleared={dueCleared}
          pendingAmount={Number(duePendingAmount)}
          remainingCA={Number(remainingAmount)}
          isZeroPayment={isZeroPayment}
          primaryButtonText={"Proceed to pay"}
          buttonColor={"bg-p_primary"}
          buttonLoading={loading}
          handleclose={handleClose}
          buttonWidth="w-[260px]"
        />
      )}
      {isVerificationModelOpen && (
        <PinVerificationModel
          handleClose={handleVerificationClose}
          pin={pin}
          setPin={setPin}
          handleDropdownChange={handleInputChange}
          selectedDropDownValue={formData?.pin_interval}
          isDontAskAgainChecked={formData?.isDontAskAgainChecked}
          handleClickVerify={() => handleClickVerify(true)}
          verifyLoading={verifyLoading}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          dropdownError={errors?.pin_interval}
          disableOutsideClick={true}
        />
      )}

      {/* Transaction receipt view */}
      {isTransactionReceipt && (
        <ETopUpTransactionReceipt
          details={responseData}
          handleClickDone={handleClickDone}
          defaultPaymentType={defaultPaymentType}
        />
      )}
    </div>
  );
}